import {
  failureOf,
  restoreSubscription,
  tagFailure,
  withFailure,
  readOwnerUid,
} from '@forest/paywall-core'
import { getAuthenticatedUserInfo } from '~/lib/get-authenticated-user-info'
import { openIntercom } from '~/lib/intercom'
import { returnFromPaywall } from '~/lib/return-from-paywall'
import { m } from '~/paraglide/messages'
import { platform } from '~/params'
import { api } from '~/services/api'
import { native } from '@forest/native-services'
import { dialog } from '~/store/dialog'
import { classifyRestoreError } from './restore-helpers'
import { showSubscriptionConflictDialog } from './subscription-conflict-dialog'

/**
 * Runs the restore-purchase flow: reconcile store transactions/entitlements
 * with the backend, then surface a result dialog. On success the user is now
 * subscribed, so the success dialog dismisses the whole paywall.
 *
 * Shared by the standalone "Restore Purchases" button and the post-payment
 * failure recovery path in {@link useTrialMutation}.
 */
export async function runRestore() {
  try {
    const userInfo = await getAuthenticatedUserInfo().catch(
      (error: unknown) => {
        throw tagFailure(error, 'restore_auth')
      },
    )
    await restoreSubscription({
      api,
      userId: userInfo.user_id,
      platform,
      getAllUnclosedTransactions: native.iap.getAllUnclosedTransactions,
      getAllEntitlements: native.iap.getAllEntitlements,
      closeTransaction: native.iap.closeTransaction,
    })
    dialog.close()
    dialog.open({
      title: m.dialog_restore_success_title(),
      description: m.dialog_restore_success_content(),
      button: m.awesome(),
      action: () => {
        native.user.refreshServiceLevel()
        returnFromPaywall()
      },
    })
  } catch (error) {
    dialog.close()
    await showRestoreErrorDialog(error)
  }
}

export async function showRestoreErrorDialog(error: unknown) {
  const branch = classifyRestoreError(error, platform)
  const failure = failureOf(error, 'restore')

  switch (branch.kind) {
    case 'no_transaction':
      dialog.open({
        title: m.dialog_restore_error_title(),
        description: withFailure(
          platform === 'china_android'
            ? m.dialog_restore_no_transaction_content_cna()
            : m.dialog_restore_no_transaction_content({
                app_store_account:
                  platform === 'ios' ? m.apple_id() : m.google_play_store_id(),
              }),
          failure,
        ),
        button: m.OK(),
      })
      return

    case 'owner_uid_conflict':
      await showSubscriptionConflictDialog(await readOwnerUid(error), failure)
      return

    case 'forest_account_mismatch':
      dialog.open({
        title: m.dialog_restore_error_title(),
        description: withFailure(
          m.dialog_restore_error_forest_account_content({
            app_store_account:
              platform === 'ios' ? m.apple_id() : m.google_play_store_id(),
          }),
          failure,
        ),
        button: m.OK(),
      })
      return

    case 'transaction_expired':
      dialog.open({
        title: m.dialog_transaction_expired_title(),
        description: withFailure(
          m.dialog_transaction_expired_content(),
          failure,
        ),
        button: m.OK(),
      })
      return

    case 'unexpected_http':
      dialog.open({
        title: m.dialog_restore_error_title(),
        description: withFailure(
          m.dialog_unknown_error_content({ error_code: failure.code }),
          failure,
          { codeInDescription: true },
        ),
        button: m.contact_cs(),
        action: () => {
          openIntercom({
            node: 'restore_purchase_unexpected_http_error',
            orderId: failure.purchaseId?.toString(),
            additional: { error_code: failure.code },
          })
        },
      })
      return

    case 'connection':
      dialog.open({
        title: m.dialog_no_internet_title(),
        description: withFailure(m.dialog_no_internet_content(), failure),
        button: m.OK(),
      })
      return

    case 'unexpected':
      dialog.open({
        title: m.dialog_restore_error_title(),
        description: withFailure(
          m.dialog_unknown_error_content({ error_code: failure.code }),
          failure,
          { codeInDescription: true },
        ),
        button: m.contact_cs(),
        action: () => {
          openIntercom({
            node: 'restore_purchase_unexpected_error',
            orderId: failure.purchaseId?.toString(),
            additional: { error_code: failure.code },
          })
        },
      })
      return
  }
}
